import { addDays, addWeeks, addMonths } from "date-fns";
import { Users } from "lucide-react";
import { useMemo } from "react";
import { DateRange } from "react-day-picker";
import { Line, LineChart, XAxis, YAxis, CartesianGrid } from "recharts";
import {
    getDateKeyForGrouping,
    getWeekStartKey,
    getMonthKeyForGrouping,
    getGroupingGranularity,
    formatDateForDisplay,
    formatWeekRange,
} from "./utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent,
} from "@/components/ui/chart";
import { IUserJob } from "@/types";

interface UserJobsAnalyticsProps {
    data?: IUserJob[];
    dateRange: DateRange;
}

export const UserJobsAnalytics = ({
    data,
    dateRange,
}: UserJobsAnalyticsProps) => {
    const from = useMemo(() => dateRange?.from ?? new Date(), [dateRange]);
    const to = useMemo(() => dateRange?.to ?? new Date(), [dateRange]);
    const granularity = getGroupingGranularity(from, to);

    const createJobTrendsData = useMemo(() => {
        if (!data) return [];

        // Group by granularity
        const groupKeyFn =
            granularity === "month"
                ? getMonthKeyForGrouping
                : granularity === "week"
                  ? getWeekStartKey
                  : getDateKeyForGrouping;

        // Count user jobs per group
        const groupedUserJobs = data.reduce(
            (acc: Record<string, number>, userJob: IUserJob) => {
                const key = groupKeyFn(userJob.createdAt);
                acc[key] = (acc[key] || 0) + 1;
                return acc;
            },
            {},
        );

        // Generate complete date range
        const generateDateRange = () => {
            const dates: string[] = [];
            let currentDate = new Date(from);
            const endDate = new Date(to);

            while (currentDate <= endDate) {
                let key: string;

                if (granularity === "month") {
                    key = getMonthKeyForGrouping(currentDate);
                    currentDate = addMonths(currentDate, 1);
                } else if (granularity === "week") {
                    key = getWeekStartKey(currentDate);
                    currentDate = addWeeks(currentDate, 1);
                } else {
                    key = getDateKeyForGrouping(currentDate);
                    currentDate = addDays(currentDate, 1);
                }

                if (!dates.includes(key)) {
                    dates.push(key);
                }
            }

            return dates;
        };

        const completeDateRange = generateDateRange();

        // Build chart data with all dates in range
        return completeDateRange
            .map(key => ({
                key: String(key),
                userJobs: groupedUserJobs[key] || 0,
            }))
            .sort((a, b) => String(a.key).localeCompare(String(b.key)));
    }, [data, granularity, from, to]);

    const chartConfig = {
        userJobs: {
            label: "User Jobs",
            theme: {
                light: "hsl(var(--admin-chart-1))",
                dark: "hsl(var(--admin-chart-1))",
            },
        },
    };

    const totalUserJobs = createJobTrendsData.reduce(
        (sum, item) => sum + item.userJobs,
        0,
    );

    return (
        <Card className="h-full">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    User Jobs Analytics
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="rounded-lg bg-admin-muted p-4">
                    <div className="mb-2 flex items-center justify-between">
                        <div className="flex flex-col">
                            <h4 className="text-sm font-semibold">
                                User Jobs Created Trends Analysis
                            </h4>
                        </div>
                        <div className="mt-4 flex items-center gap-4 pr-4 text-xs text-admin-muted-foreground">
                            <span className="font-semibold">
                                Total User Jobs:
                            </span>{" "}
                            {totalUserJobs}
                        </div>
                    </div>

                    {createJobTrendsData.length > 0 ? (
                        <ChartContainer
                            config={chartConfig}
                            className="h-64 w-full"
                        >
                            <LineChart
                                data={createJobTrendsData}
                                {...{ overflow: "visible" }}
                            >
                                <CartesianGrid vertical={false} />
                                <XAxis
                                    tickMargin={10}
                                    angle={granularity === "day" ? -45 : 0}
                                    dataKey="key"
                                    tick={{ fontSize: 10 }}
                                    tickFormatter={value => {
                                        if (granularity === "month") {
                                            const [year, month] =
                                                String(value).split("-");
                                            return `${new Date(Number(year), Number(month) - 1).toLocaleString("en-US", { month: "short", year: "2-digit" })}`;
                                        } else if (granularity === "week") {
                                            return formatWeekRange(
                                                String(value),
                                            );
                                        } else {
                                            return formatDateForDisplay(
                                                value,
                                                "MM/d",
                                            );
                                        }
                                    }}
                                />
                                <YAxis tick={{ fontSize: 12 }} />
                                <ChartTooltip
                                    content={<ChartTooltipContent />}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="userJobs"
                                    stroke="hsl(var(--admin-chart-1))"
                                    strokeWidth={2}
                                    dot={{
                                        fill: "hsl(var(--admin-chart-1))",
                                        strokeWidth: 2,
                                        r: 4,
                                    }}
                                    activeDot={{
                                        r: 6,
                                        stroke: "hsl(var(--admin-chart-1))",
                                        strokeWidth: 2,
                                    }}
                                />
                            </LineChart>
                        </ChartContainer>
                    ) : (
                        <div className="flex h-64 items-center justify-center text-admin-muted-foreground">
                            No user jobs data available for the selected period
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};
